import { useState, useEffect, useRef } from "react";
import { Pause, Play, Gauge } from "lucide-react";
import { useReading } from "../context/ReadingContext";
import useAutoFitText from "../hooks/useAutoFitText";
import Button from "./Button";
import "./ArticleCard.css";

const ArticleCard = ({
  article,
  onNext,
  isPaused,
  currentIndex,
  // totalArticles,
  resetTrigger,
  onTogglePause,
  onPrevious,
  // onJumpToIndex,
  onVisibilityChange,
  speedMultiplier = 1,
  isEditMode = false,
  onArticleChange,
  progressLineEnabled = true,
  maxBodyLength,
}) => {
  const [cursorStyle, setCursorStyle] = useState("pointer");
  const [isVisible, setIsVisible] = useState(true);
  const [localIsPaused, setLocalIsPaused] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const { readingModeEnabled } = useReading();
  const textareaRef = useRef(null);
  const swipeStartRef = useRef(null);
  const swipeHandledRef = useRef(false);
  const lastSwipeNavigationTimeRef = useRef(0);

  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const startTimeRef = useRef(null);
  const pausedTimeRef = useRef(null);
  const onNextRef = useRef(onNext);
  const hasCalledNextRef = useRef(false);

  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  useEffect(() => {
    if (!article) return;

    const wordsCount = article.body.split(" ").length;
    const readingTime = Math.max(
      5,
      Math.ceil((wordsCount / 200) * 60 * speedMultiplier),
    );

    setFadeOut(false);
    setTimeLeft(readingTime);
    setTotalTime(readingTime);
    startTimeRef.current = performance.now();
    pausedTimeRef.current = null;
    hasCalledNextRef.current = false;
  }, [article, currentIndex, resetTrigger, speedMultiplier]);

  useEffect(() => {
    if (!totalTime) return;

    const updateTimer = () => {
      if (isPaused) {
        if (!pausedTimeRef.current) {
          pausedTimeRef.current = performance.now();
        }
        return;
      }

      if (pausedTimeRef.current) {
        const pausedDuration = performance.now() - pausedTimeRef.current;
        startTimeRef.current += pausedDuration;
        pausedTimeRef.current = null;
      }

      const elapsed = (performance.now() - startTimeRef.current) / 1000;
      const newTimeLeft = Math.max(0, totalTime - elapsed);

      setTimeLeft(newTimeLeft);

      if (elapsed >= totalTime - 1.5) {
        setFadeOut(true);
      }

      if (elapsed >= totalTime && !hasCalledNextRef.current) {
        hasCalledNextRef.current = true;
        onNextRef.current();
      }
    };

    const timer = setInterval(updateTimer, 100);
    return () => clearInterval(timer);
  }, [totalTime, isPaused]);

  useEffect(() => {
    setLocalIsPaused(isPaused);

    if (isPaused) {
      setShowPauseIcon(true);
      setTimeout(() => setShowPauseIcon(false), 1000);
    } else if (localIsPaused !== isPaused) {
      setShowPlayIcon(true);
      setTimeout(() => setShowPlayIcon(false), 1000);
    }
  }, [isPaused, localIsPaused]);

  // Reading mode visibility logic
  useEffect(() => {
    // In reading mode, hide navigation arrows
    if (readingModeEnabled) {
      setIsVisible(false);
      onVisibilityChange?.(false);
    } else {
      // Outside reading mode, controls are always visible
      setIsVisible(true);
      onVisibilityChange?.(true);
    }
  }, [onVisibilityChange, readingModeEnabled]);

  const handleMouseMove = (e) => {
    if (
      e.target.closest(".articlecard-bottom-controls") ||
      e.target.closest(".articlecard-index") ||
      e.target.closest(".articlecard-progress-bar")
    ) {
      setCursorStyle("default");
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;
    const position = x / width;
    const verticalPosition = y / height;

    if (verticalPosition > 0.65) {
      setCursorStyle("default");
      return;
    }

    if (position < 0.15) {
      setCursorStyle(
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24'%3E%3Cpath fill='%2394a3b8' d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z'/%3E%3C/svg%3E\") 16 16, pointer",
      );
    } else if (position > 0.85) {
      setCursorStyle(
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24'%3E%3Cpath fill='%2394a3b8' d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'/%3E%3C/svg%3E\") 16 16, pointer",
      );
    } else {
      setCursorStyle("pointer");
    }
  };

  const isNavigationGestureTarget = (target) =>
    target.closest(".articlecard-bottom-controls") ||
    target.closest(".articlecard-index") ||
    target.closest(".articlecard-progress-bar") ||
    target.closest(".articlecard-nav-btn");

  const startSwipe = (x, y, target) => {
    if (isEditMode || isNavigationGestureTarget(target)) {
      return;
    }

    swipeStartRef.current = {
      x,
      y,
    };
  };

  const updateSwipe = (x, y) => {
    const start = swipeStartRef.current;
    if (!start) return;

    const deltaX = x - start.x;
    const deltaY = y - start.y;

    if (Math.abs(deltaY) > 35 && Math.abs(deltaY) > Math.abs(deltaX)) {
      swipeStartRef.current = null;
    }
  };

  const completeSwipe = (x, y) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;

    if (!start || isEditMode) {
      return false;
    }

    const deltaX = x - start.x;
    const deltaY = y - start.y;
    const isHorizontalSwipe =
      Math.abs(deltaX) >= 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;

    if (!isHorizontalSwipe) {
      return false;
    }

    swipeHandledRef.current = true;
    window.setTimeout(() => {
      swipeHandledRef.current = false;
    }, 300);

    const currentTime = Date.now();
    if (currentTime - lastSwipeNavigationTimeRef.current < 300) {
      return true;
    }
    lastSwipeNavigationTimeRef.current = currentTime;

    if (deltaX < 0) {
      onNext?.(false);
    } else {
      onPrevious?.();
    }

    return true;
  };

  const handlePointerDown = (e) => {
    if (e.pointerType !== "touch") {
      return;
    }

    startSwipe(e.clientX, e.clientY, e.target);
  };

  const handlePointerMove = (e) => {
    if (e.pointerType !== "touch") return;
    updateSwipe(e.clientX, e.clientY);
  };

  const handlePointerUp = (e) => {
    if (e.pointerType !== "touch") {
      return;
    }

    completeSwipe(e.clientX, e.clientY);
  };

  const handlePointerCancel = () => {
    swipeStartRef.current = null;
  };

  const handleTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;

    startSwipe(touch.clientX, touch.clientY, e.target);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;

    updateSwipe(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;

    if (completeSwipe(touch.clientX, touch.clientY)) {
      e.preventDefault();
    }
  };

  const handleTouchCancel = () => {
    swipeStartRef.current = null;
  };

  const handleCardClick = (e) => {
    if (swipeHandledRef.current) {
      e.preventDefault();
      return;
    }

    if (
      isNavigationGestureTarget(e.target)
    ) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;
    const clickPosition = x / width;
    const verticalPosition = y / height;

    if (verticalPosition > 0.65) {
      return;
    }

    if (clickPosition < 0.15) {
      onPrevious?.();
    } else if (clickPosition > 0.85) {
      onNext?.(false);
    } else {
      handleTogglePause();
    }
  };

  const handleTogglePause = () => {
    const newPauseState = !localIsPaused;
    setLocalIsPaused(newPauseState);

    if (newPauseState) {
      setShowPauseIcon(true);
      setTimeout(() => setShowPauseIcon(false), 1000);
    } else {
      setShowPlayIcon(true);
      setTimeout(() => setShowPlayIcon(false), 1000);
    }

    onTogglePause();
  };

  const { containerRef: fitContainerRef, textRef: fitTextRef } = useAutoFitText(article?.body, { maxFontRem: 1.4, minFontRem: 0.8 });

  const handlePrevious = () => {
    onPrevious();
  };

  const handleNext = () => {
    onNext(false);
  };

  if (!article) {
    return (
      <div className="articlecard-card">
        <div className="articlecard-content">
          <p>No articles available</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="articlecard-card"
      onClick={handleCardClick}
      onMouseMove={handleMouseMove}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onTouchStartCapture={handleTouchStart}
      onTouchMoveCapture={handleTouchMove}
      onTouchEndCapture={handleTouchEnd}
      onTouchCancelCapture={handleTouchCancel}
      style={{ cursor: cursorStyle }}
    >
      <div className="articlecard-content" ref={fitContainerRef}>
        {isEditMode ? (
          <textarea
            ref={textareaRef}
            value={article.body || ""}
            onChange={(e) => onArticleChange?.("body", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            maxLength={maxBodyLength}
            className="articlecard-edit-textarea"
          />
        ) : (
          <p ref={fitTextRef} className={fadeOut ? "fade-out" : ""}>{article.body}</p>
        )}
        {showPauseIcon && (
          <div className="articlecard-pause-overlay" key="pause">
            <Pause size={50} strokeWidth={2.5} />
          </div>
        )}
        {showPlayIcon && (
          <div className="articlecard-pause-overlay" key="play">
            <Play size={50} strokeWidth={2.5} />
          </div>
        )}
      </div>

      {/* Bottom controls */}
      {/* <div
        className={`articlecard-bottom-controls ${
          !isVisible ? "articlecard-controls-hidden" : ""
        }`}
      >
        <div className="articlecard-controls-row">
          <form onSubmit={handleJumpSubmit} className="articlecard-jump-form">
            <Button
              className="articlecard-btn articlecard-btn-play"
              onClick={(e) => {
                e.preventDefault();
                handleTogglePause();
              }}
              type="button"
              title=""
            >
              {localIsPaused ? "▷" : "||"}
            </Button>
            <input
              type="number"
              min="1"
              max={totalArticles}
              value={jumpIndex}
              onChange={(e) => setJumpIndex(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="1"
              className="articlecard-jump-input"
              onFocus={handleFocus}
            />
            <Button
              className="articlecard-btn articlecard-btn-play articlecard-btn-go"
              onClick={(e) => {
                e.preventDefault();
                const indexToJump = jumpIndex || "1"; // Use 1 as default if empty
                const index = parseInt(indexToJump);
                if (index >= 1 && index <= totalArticles) {
                  if (localIsPaused) {
                    setLocalIsPaused(false);
                    onTogglePause();
                  }
                  onJumpToIndex(index - 1);
                  setJumpIndex(indexToJump);
                }
              }}
              type="button"
              title=""
            >
              GO
            </Button>
          </form>
        </div>
      </div> */}

      <div
        className="articlecard-progress-bar"
        style={{ visibility: progressLineEnabled ? "visible" : "hidden" }}
      >
        <div
          className="articlecard-progress-fill"
          style={{
            width: `${
              totalTime > 0 ? ((totalTime - timeLeft) / totalTime) * 100 : 0
            }%`,
          }}
        />
      </div>

      {/* <div
        className={`articlecard-index ${
          !isVisible ? "articlecard-controls-hidden" : ""
        }`}
      >
        {currentIndex + 1} / {totalArticles || "N/A"}
      </div> */}

      {/* Side navigation buttons */}
      <button
        className={`articlecard-nav-btn articlecard-nav-prev ${
          !isVisible ? "articlecard-controls-hidden" : ""
        }`}
        onClick={handlePrevious}
        title=""
      >
        ‹‹
      </button>

      <button
        className={`articlecard-nav-btn articlecard-nav-next ${
          !isVisible ? "articlecard-controls-hidden" : ""
        }`}
        onClick={handleNext}
        title=""
      >
        ››
      </button>
    </div>
  );
};

export default ArticleCard;
